import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, Clock, LocateFixed, Search } from "lucide-react";
import { detectTimezone, listTimezones, tzOffset } from "../lib/timezone";
import { useI18n } from "../lib/i18n";

interface Props {
  value: string;
  onChange: (tz: string) => void;
  id?: string;
  /** compact = pill-style button for the top-right toolbar */
  compact?: boolean;
}

/**
 * Searchable IANA timezone picker with live UTC offsets. Popover uses a React
 * portal + fixed positioning so it always sits above every card no matter
 * where the trigger lives on the page.
 */
export default function TimezonePicker({ value, onChange, id, compact = false }: Props) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const btnRef = useRef<HTMLButtonElement>(null);
  const [rect, setRect] = useState<DOMRect | null>(null);

  const all = useMemo(() => listTimezones(), []);
  // What the device says it is in. Computed once -- it does not change while
  // the popover is open, and reading it is a synchronous Intl call.
  const detected = useMemo(() => detectTimezone(), []);
  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return all;
    return all.filter((tz) => tz.toLowerCase().includes(s));
  }, [all, search]);

  const currentOff = tzOffset(value);

  useLayoutEffect(() => {
    if (!open) return;
    const update = () => {
      if (btnRef.current) setRect(btnRef.current.getBoundingClientRect());
    };
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const popoverWidth = 460;
  const popoverHeight = 320;

  return (
    <>
      <button
        ref={btnRef}
        id={id}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={
          compact
            ? "inline-flex items-center gap-1.5 rounded-lg border border-glass bg-surface-1 px-3 py-2 text-xs font-semibold text-parchment hover:bg-surface-2 hover:border-glass-mid transition"
            : "input flex items-center justify-between gap-2 text-start"
        }
      >
        {compact ? (
          <>
            <Clock className="w-3.5 h-3.5 text-accent" />
            <span className="tabular">{value.split("/").pop()}</span>
            <span className="text-parchment-muted tabular">{currentOff || "—"}</span>
          </>
        ) : (
          <>
            <span className="truncate">
              <span className="font-mono text-parchment">{value}</span>{" "}
              <span className="text-parchment-muted">({currentOff || "—"})</span>
            </span>
            <span className="text-parchment-muted text-xs shrink-0">{t("tz.change")}</span>
          </>
        )}
      </button>

      {open &&
        rect &&
        createPortal(
          <>
            <button
              type="button"
              aria-hidden
              onClick={() => setOpen(false)}
              className="fixed inset-0 z-[100] cursor-default"
            />
            <div
              className="fixed z-[110] rounded-xl border border-glass-mid shadow-lift overflow-hidden"
              style={{
                top: Math.min(rect.bottom + 6, window.innerHeight - popoverHeight - 12),
                left: Math.max(
                  12,
                  Math.min(rect.left, window.innerWidth - popoverWidth - 12),
                ),
                width: `min(${popoverWidth}px, calc(100vw - 1.5rem))`,
                backgroundColor: "rgb(var(--bg-800))",
              }}
            >
              <div className="p-2 border-b border-glass">
                <label className="relative block">
                  <Search className="w-4 h-4 absolute start-3 top-1/2 -translate-y-1/2 text-parchment-muted" />
                  <input
                    autoFocus
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={t("tz.searchPlaceholder")}
                    className="input ps-9"
                  />
                </label>
              </div>

              {detected && (
                <button
                  type="button"
                  onClick={() => {
                    onChange(detected);
                    setOpen(false);
                    setSearch("");
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-start border-b border-glass text-parchment hover:bg-surface-2 transition"
                >
                  <LocateFixed className="w-4 h-4 text-accent shrink-0" />
                  <span className="flex-1 min-w-0">
                    <span className="block">{t("tz.useSystem")}</span>
                    <span className="block font-mono text-xs text-parchment-muted truncate">
                      {detected} ({tzOffset(detected) || "—"})
                    </span>
                  </span>
                  {detected === value && <Check className="w-4 h-4 text-accent shrink-0" />}
                </button>
              )}

              <ul className="max-h-72 overflow-y-auto">
                {filtered.slice(0, 200).map((tz) => {
                  const active = tz === value;
                  const off = tzOffset(tz);
                  return (
                    <li key={tz}>
                      <button
                        type="button"
                        onClick={() => {
                          onChange(tz);
                          setOpen(false);
                          setSearch("");
                        }}
                        className={
                          "w-full flex items-center gap-2 px-3 py-2 text-sm text-start transition " +
                          (active ? "bg-gold-500/15 text-accent" : "text-parchment hover:bg-surface-2")
                        }
                      >
                        <span className="w-4">
                          {active && <Check className="w-4 h-4 text-accent" />}
                        </span>
                        <span className="font-mono flex-1 truncate">{tz}</span>
                        <span className="text-parchment-muted text-xs tabular">{off}</span>
                      </button>
                    </li>
                  );
                })}
                {filtered.length === 0 && (
                  <li className="px-4 py-6 text-sm text-parchment-muted text-center">
                    {t("tz.noMatches")}
                  </li>
                )}
                {filtered.length > 200 && (
                  <li className="px-4 py-2 text-xs text-parchment-muted text-center border-t border-hairline">
                    {t("tz.showing", { total: filtered.length })}
                  </li>
                )}
              </ul>
            </div>
          </>,
          document.body,
        )}
    </>
  );
}
